import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { encuestaEventoApi } from '../api/seguimiento'

/**
 * Encuesta de EVENTO (Ligier Experience #8) — página pública por token del
 * asistente. Formato corto con RAMIFICACIÓN (5 preguntas, <1 min):
 *   P1 ¿Te gustó? Sí/No — viene one-click EN el mail (?g=si|no)
 *   Ambas ramas: organización 1-5 ★
 *   Rama SÍ: qué te gustó (chips + otro) · ¿recomendarías? · qué mejorar (opc.)
 *   Rama NO: qué no te gustó · ¿volverías si lo mejoramos? · qué cambiar
 */

const INK = '#111'
const VERDE = '#2D6A3F'

function Escala5({ valor, onChange }) {
  const [hover, setHover] = useState(0)
  return (
    <div style={st.stars}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          style={{ ...st.star, color: n <= (hover || valor) ? VERDE : '#d1d5db' }} aria-label={`${n} de 5`}>★</button>
      ))}
    </div>
  )
}

function Pregunta({ titulo, children }) {
  return (
    <div style={st.pregunta}>
      <p style={st.preguntaTitulo}>{titulo}</p>
      {children}
    </div>
  )
}

function Chips({ opciones, valor, onChange, multi = false }) {
  const activo = (v) => multi ? valor.includes(v) : valor === v
  const toggle = (v) => multi
    ? onChange(activo(v) ? valor.filter(x => x !== v) : [...valor, v])
    : onChange(v)
  return (
    <div style={st.chips}>
      {opciones.map(o => (
        <button key={o.valor} type="button" onClick={() => toggle(o.valor)}
          style={{ ...st.chip, ...(activo(o.valor) ? st.chipOn : {}) }}>{o.label}</button>
      ))}
    </div>
  )
}

const LE_GUSTO = [
  { valor: 'curaduria', label: 'Curaduría de bodegas y vinos' },
  { valor: 'ambientacion', label: 'Ambientación y producción del salón' },
  { valor: 'staff', label: 'Atención del staff' },
  { valor: 'qr', label: 'Sistema de degustación con QR' },
  { valor: 'otro', label: 'Otro' },
]
const RECOMENDARIAS = [
  { valor: 'si_seguro', label: 'Sí, seguro' },
  { valor: 'probablemente', label: 'Probablemente' },
  { valor: 'no_seguro', label: 'No estoy seguro' },
]
const VOLVERIAS = [
  { valor: 'si', label: 'Sí' },
  { valor: 'tal_vez', label: 'Tal vez' },
  { valor: 'no', label: 'No' },
]

export default function EncuestaEventoPage() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()
  const [info, setInfo] = useState(null)
  const [error, setError] = useState('')
  const [listo, setListo] = useState(false)
  const [registrado, setRegistrado] = useState(false)

  const [gusto, setGusto] = useState('')
  const [organizacion, setOrganizacion] = useState(0)
  const [leGusto, setLeGusto] = useState([])
  const [leGustoOtro, setLeGustoOtro] = useState('')
  const [recomendarias, setRecomendarias] = useState('')
  const [noGusto, setNoGusto] = useState('')
  const [volverias, setVolverias] = useState('')
  const [comentario, setComentario] = useState('')
  const [falta, setFalta] = useState('')
  const [enviando, setEnviando] = useState(false)

  // Respuesta elegida EN EL MAIL (?g=si|no): se registra apenas carga. Vía JS
  // a propósito: los escáneres de links cargan el HTML pero no lo ejecutan.
  const gustoDelMail = useRef(null)
  if (gustoDelMail.current === null) {
    const g = searchParams.get('g')
    gustoDelMail.current = g === 'si' || g === 'no' ? g : ''
  }

  useEffect(() => {
    const delMail = gustoDelMail.current || null
    encuestaEventoApi.obtener(token)
      .then(({ data }) => {
        setInfo(data)
        if (data.respondida && delMail === null) setListo(true)
      })
      .catch(() => setError('No encontramos esta encuesta. Revisá el enlace del correo.'))

    if (delMail !== null) {
      setGusto(delMail)
      encuestaEventoApi.responder(token, { gusto: delMail })
        .then(() => setRegistrado(true))
        .catch(() => {})
    }
  }, [token])

  const elegirGusto = (g) => {
    setGusto(g)
    if (registrado) encuestaEventoApi.responder(token, { gusto: g }).catch(() => {})
  }

  const enviar = async () => {
    if (!gusto) { setFalta('Contanos primero si te gustó el evento.'); return }
    if (!organizacion || (gusto === 'si' ? !recomendarias : !volverias)) {
      setFalta('Te falta contestar alguna pregunta.')
      return
    }
    setEnviando(true); setFalta('')
    try {
      await encuestaEventoApi.responder(token, {
        gusto,
        organizacion,
        ...(gusto === 'si'
          ? { le_gusto: leGusto, le_gusto_otro: leGusto.includes('otro') ? (leGustoOtro || null) : null, recomendarias }
          : { no_gusto: noGusto || null, volverias }),
        comentario: comentario || null,
      })
      setListo(true)
    } catch (e) {
      setFalta(e?.response?.data?.message || 'No se pudo enviar. Probá de nuevo.')
      setEnviando(false)
    }
  }

  return (
    <div style={st.page}>
      <div style={st.card}>
        <div style={st.logoWrap}>
          <img src="/favicon.png" alt="Ligier" style={st.logoImg} />
        </div>

        {error && <p style={st.error}>{error}</p>}
        {!error && !listo && !info && <p style={st.sub}>Cargando…</p>}

        {!error && listo && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p style={{ fontSize: '1.4rem', margin: 0 }}>🍷</p>
            <h1 style={st.h1}>¡Gracias por tu tiempo!</h1>
            <p style={st.sub}>Leemos todas las respuestas — nos ayudan a que la próxima edición sea aún mejor.</p>
          </div>
        )}

        {!error && !listo && info && (
          <>
            <p style={st.eyebrow}>Ligier Experience N°8</p>
            <h1 style={st.h1}>Tu opinión nos importa</h1>
            {registrado ? (
              <p style={{ ...st.sub, color: VERDE, fontWeight: 600 }}>
                ✓ Tu respuesta quedó registrada. Completá el resto en menos de un minuto.
              </p>
            ) : (
              <p style={st.sub}>Gracias por haber sido parte. Te toma menos de 1 minuto.</p>
            )}

            <Pregunta titulo="¿Te gustó Ligier Experience N°8?">
              <div style={st.chips}>
                <button type="button" onClick={() => elegirGusto('si')}
                  style={{ ...st.bigBtn, ...(gusto === 'si' ? st.chipOn : {}) }}>Sí</button>
                <button type="button" onClick={() => elegirGusto('no')}
                  style={{ ...st.bigBtn, ...(gusto === 'no' ? st.chipOn : {}) }}>No</button>
              </div>
            </Pregunta>

            {gusto && (
              <>
                <Pregunta titulo="¿Cómo calificarías la organización y logística del evento?">
                  <Escala5 valor={organizacion} onChange={setOrganizacion} />
                </Pregunta>

                {gusto === 'si' && (
                  <>
                    <Pregunta titulo="¿Qué fue lo que más te gustó?">
                      <Chips opciones={LE_GUSTO} valor={leGusto} onChange={setLeGusto} multi />
                      {leGusto.includes('otro') && (
                        <input data-nocaps value={leGustoOtro} onChange={e => setLeGustoOtro(e.target.value)}
                          maxLength={300} style={st.input} placeholder="Contanos qué" />
                      )}
                    </Pregunta>
                    <Pregunta titulo="¿Recomendarías Ligier Experience a un amigo o familiar?">
                      <Chips opciones={RECOMENDARIAS} valor={recomendarias} onChange={setRecomendarias} />
                    </Pregunta>
                    <Pregunta titulo="¿Qué te gustaría que mejoremos para la próxima edición?">
                      <textarea data-nocaps value={comentario} onChange={e => setComentario(e.target.value)} maxLength={2000}
                        style={st.textarea} placeholder="Contanos lo que quieras (opcional)" />
                    </Pregunta>
                  </>
                )}

                {gusto === 'no' && (
                  <>
                    <Pregunta titulo="¿Qué fue lo que menos te gustó?">
                      <textarea data-nocaps value={noGusto} onChange={e => setNoGusto(e.target.value)} maxLength={1000}
                        style={{ ...st.textarea, height: 70 }} placeholder="Contanos brevemente" />
                    </Pregunta>
                    <Pregunta titulo="¿Volverías a una próxima edición si mejoramos eso?">
                      <Chips opciones={VOLVERIAS} valor={volverias} onChange={setVolverias} />
                    </Pregunta>
                    <Pregunta titulo="¿Qué deberíamos cambiar para la próxima edición?">
                      <textarea data-nocaps value={comentario} onChange={e => setComentario(e.target.value)} maxLength={2000}
                        style={st.textarea} placeholder="Tu respuesta" />
                    </Pregunta>
                  </>
                )}

                {falta && <p style={st.error}>{falta}</p>}
                <button onClick={enviar} disabled={enviando} style={st.btn}>
                  {enviando ? 'Enviando…' : 'Enviar respuestas'}
                </button>
              </>
            )}
          </>
        )}
      </div>
      <p style={st.legal}>Vinoteca Ligier · Buenos Aires, Argentina</p>
    </div>
  )
}

const st = {
  page: { minHeight: '100vh', background: '#f4f1ec', padding: '24px 12px', fontFamily: 'Arial, Helvetica, sans-serif' },
  card: { maxWidth: 520, margin: '0 auto', background: '#fff', padding: '26px 22px 30px' },
  logoWrap: { textAlign: 'center', borderBottom: `3px solid ${VERDE}`, paddingBottom: 14, marginBottom: 20 },
  logoImg: { width: 110, height: 'auto', display: 'inline-block' },
  eyebrow: { textAlign: 'center', fontSize: '0.7rem', letterSpacing: 2, textTransform: 'uppercase', color: '#8a8578', margin: 0 },
  h1: { textAlign: 'center', fontSize: '1.35rem', fontWeight: 700, color: INK, margin: '8px 0 4px', lineHeight: 1.3 },
  sub: { textAlign: 'center', fontSize: '0.9rem', color: '#666', margin: '0 0 8px' },
  pregunta: { borderTop: '1px solid #eee', marginTop: 18, paddingTop: 16 },
  preguntaTitulo: { fontSize: '0.95rem', fontWeight: 400, color: '#222', margin: '0 0 10px', textAlign: 'center' },
  stars: { display: 'flex', justifyContent: 'center', gap: 8 },
  star: { background: 'none', border: 'none', fontSize: '2rem', cursor: 'pointer', lineHeight: 1, padding: 0 },
  chips: { display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' },
  chip: { padding: '10px 14px', border: '1px solid #d1d5db', background: '#fff', color: '#333', cursor: 'pointer', borderRadius: 999, fontSize: '0.85rem' },
  chipOn: { background: INK, borderColor: INK, color: '#fff' },
  bigBtn: { minWidth: 110, padding: '14px 20px', border: '1px solid #d1d5db', background: '#fff', color: '#333', cursor: 'pointer', borderRadius: 999, fontSize: '1.05rem', fontWeight: 700 },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: '0.9rem', fontFamily: 'inherit', marginTop: 10 },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: '0.9rem', fontFamily: 'inherit', height: 90, resize: 'vertical' },
  btn: { width: '100%', marginTop: 20, background: INK, color: '#fff', border: 'none', padding: '14px 0', fontSize: '0.85rem', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer' },
  error: { textAlign: 'center', color: '#991b1b', fontSize: '0.9rem', margin: '12px 0 0' },
  legal: { textAlign: 'center', fontSize: '0.72rem', color: '#9ca3af', margin: '18px 8px 0' },
}
